import {
  childDependents,
  messageType,
  spouseDependent,
  validatePrimaryInsured,
  validatePrimaryDateofBirth,
  validatePrimaryAddress,
  validatePrimaryZip,
  validateAgents,
  validateAppSignature,
  validateAgentSignature,
  getStateSigned,
  validatePACTransit,
  validatePACAccount,
  validatePrimarySex,
  validatePrimarySSN,
  validateAppSignDate,
  validateBeneficiaryInformation,
  validateCaseNumber,
  validateControlNumber,
  validateFormNumber,
  validateIssueDate,
  validatePrimaryCity,
  validatePrimaryDateOfHire,
  validatePrimaryHeightFeet,
  validatePrimaryHeightInches,
  validatePrimarySignature,
  validatePrimaryState,
  validatePrimaryWeight,
  validateStateSigned,
  validateSpouseGender,
} from 'ApplicationEntry/components/WorkFlow/ValidateApplication';
import {AgentList} from 'ApplicationEntry/models/AgentList';
import {ApplicantDemographics, matchApplicantDemographicsByType} from 'ApplicationEntry/models/ApplicantDemographics';
import {PrimaryBeneficiaryList, matchPrimaryBeneficiaryList} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import {BillingAgent, matchBillingAgentByType} from 'ApplicationEntry/models/BillingAgent';
import {CaseDetails} from 'ApplicationEntry/models/CaseDetails';
import {DependentLists} from 'ApplicationEntry/models/DependentLists';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {PlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {GroupAccident} from 'ApplicationEntry/models/ProductsRiders/Accident/GroupAccident';
import {GroupCriticalIllness} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/GroupCriticalIllness';
import {UWQuestions} from 'ApplicationEntry/models/UnderwritingQuestions';
import {isNonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {GroupGLTERM, GroupGLTermType} from 'ApplicationEntry/models/ProductsRiders/GLTERM/GroupGLTERM';
import {GroupUL, GroupULType} from 'ApplicationEntry/models/ProductsRiders/UniversalLife/GroupUL';
import {isLoaded} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {List} from 'immutable';
import {matchULByType} from 'ApplicationEntry/models/ProductsRiders/UniversalLife/UniversalLife';
import DateUtility from 'Shared/utilities/DateUtility';
import {OwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {ValidPrimaryBeneficiary} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {GroupCancer} from 'ApplicationEntry/models/ProductsRiders/Cancer/GroupCancer';
import {GroupIWL, GroupIWLType} from 'ApplicationEntry/models/ProductsRiders/IWL/GroupIWL';
import {GroupShop} from 'ApplicationEntry/models/ProductsRiders/Shop/GroupShop';
import {Premium} from 'ApplicationEntry/models/Premium';
import {ContingentBeneficiaryList} from 'ApplicationEntry/models/ContingentBeneficiaryList';
import {GroupDisability} from 'ApplicationEntry/models/ProductsRiders/Disability/GroupDisability';
import Disability from './ProductsRiders/Ordinary/Disability';
import {matchShopByType} from 'ApplicationEntry/models/ProductsRiders/Shop/Shop';

export const groupDependentProductIdentifier = (
  caseDetails: CaseDetails,
  product: GroupAccident | GroupCancer | GroupShop,
  dependentList: DependentLists,
  isQuality: boolean
) => {
  if (!isQuality && dependentList.dependentList.size > 0 && product?.plan?.value !== '') {
    const isAccidentPolicy =
      caseDetails.productType.key === 'LoadedPolicyTypes' &&
      caseDetails.productType.selectedPolicyTypes.some(policyType => policyType.friendlyName == 'group_acc');
    const isCancerPolicy =
      caseDetails.productType.key === 'LoadedPolicyTypes' &&
      caseDetails.productType.selectedPolicyTypes.some(policyType => policyType.friendlyName == 'group_cancer');
    const isShopPolicy =
      caseDetails.productType.key === 'LoadedPolicyTypes' &&
      caseDetails.productType.selectedPolicyTypes.some(policyType => policyType.friendlyName == 'group_shop');
    if (product?.planType.value !== 'IND') {
      const depAccidentPresent = dependentList.dependentList.some(dep => dep.accidentDependent);
      const depCancerPresent = dependentList.dependentList.some(dep => dep.cancerDependent);
      const depShopPresent = dependentList.dependentList.some(dep => dep.shopDependent);
      if (isAccidentPolicy && isCancerPolicy && isShopPolicy) {
        if (depAccidentPresent == false || depCancerPresent == false || depShopPresent === false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      } else if (isAccidentPolicy && isCancerPolicy) {
        if (depAccidentPresent == false || depCancerPresent == false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      } else if (isAccidentPolicy && isShopPolicy) {
        if (depAccidentPresent == false || depShopPresent == false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      } else if (isCancerPolicy && isShopPolicy) {
        if (depCancerPresent == false || depShopPresent == false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      } else if (isCancerPolicy) {
        if (depCancerPresent == false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      } else if (isAccidentPolicy) {
        if (depAccidentPresent == false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      } else if (isShopPolicy) {
        if (depShopPresent == false) {
          return 'Dependents have been keyed but were not associated to any products. Please review.';
        }
      }
    }
    return null;
  }
};

export const validateGrpAccident = (
  accident: GroupAccident,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  primaryBeneficiaryList: PrimaryBeneficiaryList,
  contingentBeneficiaryList: ContingentBeneficiaryList,
  planCodes: PlanCodes,
  caseDetails: CaseDetails,
  questions: UWQuestions,
  owner: OwnerInfo,
  isQuality: boolean
) => {
  const messages: messageType[] = [];

  const answerCount = questions.answers.filter(answer => answer !== '').size;
  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    NewYork: NYApp => NYApp.signatureState.value,
    Group: GrpApp => GrpApp.state.value,
  })(billingAgent);

  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: GrpApp => (GrpApp.formNumber.key === 'UserAssignedFormNumber' ? GrpApp.formNumber.value : ''),
  })(billingAgent);

  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: GrpApp => GrpApp.email,
    _: () => null,
  })(applicantInfo);

  const billPayForm = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.directBill,
    Group: () => false,
    NewYork: () => false,
  })(billingAgent);

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(accident.packageName)) {
      messages.push({
        type: 'error',
        value: 'Missing accident package',
      });
    }
    if (!isNonEmptyString(accident.planType)) {
      messages.push({
        type: 'warning',
        value: 'Missing accident plan type',
      });
    }
    if (!isNonEmptyString(accident.baseUnits)) {
      messages.push({
        type: 'warning',
        value: 'Missing accident base units',
      });
    }
    if (!isNonEmptyString(accident.premium) || Number(accident.premium.value) === 0) {
      messages.push({
        type: 'warning',
        value: 'Missing accident premium',
      });
    }
    if (accident.onOffJobAccidentInsuredSpouse && accident.onOffJobAccidentSicknessInsuredSpouse) {
      messages.push({
        type: 'warning',
        value: 'Multiple spouse riders selected for accident plan',
      });
    }
    if (applicantInfo.sex === '') {
      messages.push({
        type: 'warning',
        value: 'Missing sex for primary insured',
      });
    }
    if (
      (accident.onOffJobAccidentInsuredSpouse || accident.onOffJobAccidentSicknessInsuredSpouse) &&
      !isNonEmptyString(accident.spouse)
    ) {
      messages.push({
        type: 'warning',
        value: 'Missing spouse accident rider units',
      });
    }
    if ((accident.plan.value === 'GVA1FD' || accident.plan.value === 'GVA2FD') && accident.planType.value === 'IND') {
      messages.push({
        type: 'warning',
        value: 'Invalid coverage level selected',
      });
    }
    if (stateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing Situs State',
      });
    }
    let results = validatePrimaryState(applicantInfo.state);
    if (results != null) messages.push({type: 'warning', value: results});
    if (accident.planType.value === 'FAMILY' && dependentList.key === 'InvalidDependentList') {
      messages.push({
        type: 'warning',
        value: 'Family Accident GVAP1 plan selected, but no dependents entered',
      });
    }

    const spouse = spouseDependent(dependentList)?.get(0);
    if (!spouse) {
      if (accident.onOffJobAccidentInsuredSpouse || accident.onOffJobAccidentSicknessInsuredSpouse) {
        messages.push({
          type: 'warning',
          value: 'Spouse Accident GVAP1 rider selected, but no spouse entered',
        });
      }
    }

    if (billPayForm && (Number(accident.cwa.value) === 0 || accident.cwa.value === '')) {
      messages.push({
        type: 'warning',
        value: 'Missing CWA with direct bill or PAC pay form on accident plan',
      });
    }

    if (accident.plan.value === 'GVA2FD' && dependentList.key === 'InvalidDependentList') {
      messages.push({
        type: 'warning',
        value: 'Family Accident GVAP2 plan selected, but no dependents entered',
      });
    }
  }
  if (!isQuality) {
    let results = groupDependentProductIdentifier(caseDetails, accident, dependentList, isQuality);
    if (results != null) messages.push({type: 'error', value: results});
  }

  let {billingCode} = caseDetails as ValidGroupCaseDetails;

  if (billingAgent.billingMode.value === '' && billingCode?.value === '') {
    messages.push({
      type: 'error',
      value: 'Missing billing mode',
    });
  }

  if (
    !isNonEmptyString(applicantInfo.address) ||
    !isNonEmptyString(applicantInfo.city) ||
    !isNonEmptyString(applicantInfo.state) ||
    applicantInfo.zip.value === ''
  ) {
    messages.push({
      type: 'error',
      value: 'Missing address information',
    });
  }

  if (!isNonEmptyString(billingAgent.achAccountNumber) && billingAgent.pac === true) {
    messages.push({
      type: 'error',
      value: 'Missing ACH Account number',
    });
  }

  if (!isNonEmptyString(billingAgent.achTransitNumber) && billingAgent.pac === true) {
    messages.push({
      type: 'error',
      value: 'Missing ACH Transit number',
    });
  }

  if (dependentList.dependentList.count() > 0) {
    dependentList.dependentList.forEach(dependent => {
      if (dependent.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `Please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
        });
      }
    });
  }

  if (findHeightWeightAnswer(questions)) {
    if (questions.height?.feet === '' || questions.height?.feet === undefined) {
      messages.push({
        type: 'warning',
        value: 'Missing the applicant height (feet).',
      });
    }
    if (questions.weight === '' || questions.weight === undefined) {
      messages.push({
        type: 'warning',
        value: 'Missing the applicant weight (lbs).',
      });
    }

    if (dependentList.dependentList.count() > 0) {
      dependentList.dependentList.forEach(dependent => {
        if (dependent.relationship.value.toUpperCase() === 'SPOUSE') {
          if (questions.spouseHeight?.feet === '' || questions.spouseHeight?.feet === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Spouse height (feet).',
            });
          }

          if (questions.spouseWeight === '' || questions.spouseWeight === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Spouse weight (lbs).',
            });
          }
        }
        if (dependent.relationship.value.toUpperCase() === 'CHILD') {
          if (questions.childHeight?.feet === '' || questions.childHeight?.feet === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Child height (feet).',
            });
          }

          if (questions.childWeight === '' || questions.childWeight === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Child weight (lbs).',
            });
          }
        }
      });
    }
  }

  if (primaryBeneficiaryList.primaryBeneficiaryList.size === 0) {
    messages.push({
      type: 'warning',
      value: 'Missing Primary beneficiary information',
    });
  }

  if (contingentBeneficiaryList.contingentBeneficiaryList.size === 0) {
    messages.push({
      type: 'warning',
      value: 'Missing Contingent beneficiary information',
    });
  }

  if (!isQuality) {
    if (answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
  }

  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  if (applicantInfo.socialSecurityNumber.value === '') {
    messages.push({
      type: 'warning',
      value: 'Missing social security number for primary insured',
    });
  }

  if (applicantEmailAddress?.address === '') {
    messages.push({
      type: 'warning',
      value: "Missing the applicant's Email Address",
    });
  }

  if (billingAgent.issueDate === '') {
    messages.push({
      type: 'warning',
      value: 'Missing issue date',
    });
  }

  if (!isNonEmptyString(billingAgent.controlNumber)) {
    messages.push({
      type: 'warning',
      value: 'Missing control number',
    });
  }

  if (billingAgent.dateSigned === '') {
    messages.push({
      type: 'warning',
      value: 'Missing app signed date.',
    });
  }

  if (agents.agentList.size === 0) {
    messages.push({
      type: 'warning',
      value: 'Missing agent information',
    });
  }

  if (formNumber === '') {
    messages.push({
      type: 'warning',
      value: 'Missing form number',
    });
  }

  if (!billingAgent.ink) {
    messages.push({
      type: 'info',
      value: 'Missing primary insured signature',
    });
  }

  if (agents.agentList.size > 0) {
    let signed = agents.agentList.filter(item => item.signed);

    if (signed.size === 0) {
      messages.push({
        type: 'warning',
        value: '"Agent Signed" check box was not selected',
      });
    }
  }

  dependentList.dependentList.forEach(depItem => {
    if (depItem !== null) {
      if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
        messages.push({
          type: 'info',
          value: 'Primary Insured and spouse are same sex',
        });
    }
  });

  if (spouseDependent(dependentList).size === 0 && accident.planType.value === 'INDSPS') {
    messages.push({
      type: 'warning',
      value: 'Emp+spouse coverage selected without covered spouse information',
    });
  }

  if (childDependents(dependentList).size === 0 && accident.planType.value === 'INDCH') {
    messages.push({
      type: 'warning',
      value: 'Emp+child coverage selected without covered dependent information',
    });
  }

  if (dependentList.dependentList.size > 0 && accident.planType.value === 'IND') {
    messages.push({
      type: 'warning',
      value: 'Individual coverage selected with covered dependents.',
    });
  }

  const depCount = dependentList.dependentList.filter(dep => dep.dob === '').size;

  if (depCount > 0) {
    messages.push({
      type: 'warning',
      value: 'Invalid birthdate entered for dependent',
    });
  }

  if (applicantEmailAddress?.address === '') {
    messages.push({
      type: 'warning',
      value: "Invalid applicant's Email Address.",
    });
  }

  const resultsAgent = validateAgents(agents);
  if (resultsAgent != null) messages.push({type: 'warning', value: resultsAgent});

  const resultsOwnerSigned = validateAppSignature(owner);
  if (resultsOwnerSigned != null) messages.push({type: 'info', value: resultsOwnerSigned});

  const accidentRiderArr = [
    accident.offJobAccident,
    accident.onOffJobAccident,
    accident.offJobAccidentSickness,
    accident.onOffJobAccidentSickness,
  ];
  const riderCount = accidentRiderArr.filter(rider => rider).length;
  if (riderCount > 1) {
    messages.push({
      type: 'warning',
      value: 'Multiple individual riders selected for Accident GVAP1 plan',
    });
  }
  if (riderCount !== 0 && !isNonEmptyString(accident.employee)) {
    messages.push({
      type: 'warning',
      value: 'Missing primary insured Accident GVAP1 rider units',
    });
  }

  return messages;
};

export const findHeightWeightAnswer = (questions: UWQuestions): boolean => {
  let heightWeightAnswer = false;
  const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
  if (questionList !== '') {
    const heightWeightQuestionIndex = questionList.findIndex(
      question => question.description.toUpperCase() === 'HEIGHTWEIGHT'
    );
    heightWeightAnswer = questions.answers.get(heightWeightQuestionIndex) as boolean;
  }
  return heightWeightAnswer;
};

export const validateGrpCriiticalIllness = (
  criticalIllness: GroupCriticalIllness,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  applicantInfo: ApplicantDemographics,
  agents: AgentList,
  questions: UWQuestions,
  planCodes: PlanCodes,
  caseDetails: CaseDetails,
  isQuality: boolean
) => {
  const messages: messageType[] = [];

  const answerCount = questions.answers.filter(answer => answer !== '').size;
  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    NewYork: NYApp => NYApp.signatureState.value,
    Group: GrpApp => GrpApp.state.value,
  })(billingAgent);

  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: GrpApp => (GrpApp.formNumber.key === 'UserAssignedFormNumber' ? GrpApp.formNumber.value : ''),
  })(billingAgent);

  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: GrpApp => GrpApp.email,
    _: () => null,
  })(applicantInfo);

  const directBill = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.directBill,
    NewYork: NYApp => NYApp.directBill,
    Group: GrpApp => GrpApp.directBill,
  })(billingAgent);

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(criticalIllness.packageSource)) {
      messages.push({
        type: 'error',
        value: 'Missing Critical Illness Package',
      });
    }

    if (!isNonEmptyString(criticalIllness.planType)) {
      messages.push({
        type: 'error',
        value: 'Missing Critical Illness PlanType',
      });
    }
    if (!isNonEmptyString(criticalIllness.premium && criticalIllness.calculatedPremium)) {
      messages.push({
        type: 'warning',
        value: 'Missing Critical Illness Premium',
      });
    }
    if (criticalIllness.benefitAmount === 0) {
      messages.push({
        type: 'warning',
        value: 'Missing Critical Illness Benefit Amount',
      });
    }

    if (!isNonEmptyString(criticalIllness.insuredSC)) {
      messages.push({
        type: 'warning',
        value: 'Missing Critical Illness Primary Insured Smoking Status',
      });
    }
    if (!isNonEmptyString(applicantInfo.state)) {
      messages.push({
        type: 'warning',
        value: 'Missing state of residence for primary insured',
      });
    }
  }

  let {billingCode} = caseDetails as ValidGroupCaseDetails;

  if (billingAgent.billingMode.value === '' && billingCode?.value === '') {
    messages.push({
      type: 'error',
      value: 'Missing billing mode',
    });
  }

  if (
    !isNonEmptyString(applicantInfo.address) ||
    !isNonEmptyString(applicantInfo.city) ||
    !isNonEmptyString(applicantInfo.state) ||
    applicantInfo.zip.value === ''
  ) {
    messages.push({
      type: 'error',
      value: 'Missing address information',
    });
  }

  if (!isNonEmptyString(billingAgent.achAccountNumber) && billingAgent.pac === true) {
    messages.push({
      type: 'error',
      value: 'Missing ACH Account number',
    });
  }

  if (!isNonEmptyString(billingAgent.achTransitNumber) && billingAgent.pac === true) {
    messages.push({
      type: 'error',
      value: 'Missing ACH Transit number',
    });
  }

  if (isNonEmptyString(criticalIllness.insuredSC)) {
    const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
    if (questionList !== '') {
      const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'TOBACCOUSE');
      const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
      const insuredSCAnswer = criticalIllness.insuredSC.value === 'Smoker';
      if (tobaccoAnswer !== insuredSCAnswer) {
        messages.push({
          type: 'warning',
          value: 'Primary Insured Smoker selection and Tobacco Usage question must have the same response ',
        });
      }
    }
  }

  if (isNonEmptyString(criticalIllness.spouseSC)) {
    const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
    if (questionList !== '') {
      const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'SPOUSETOBACCOUSE');
      const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
      const spouseSCAnswer = criticalIllness.spouseSC.value === 'Smoker';
      if (tobaccoAnswer !== spouseSCAnswer) {
        messages.push({
          type: 'warning',
          value: 'Secondary Insured Smoker selection and Tobacco Usage question must have the same response ',
        });
      }
    }
  }

  if (dependentList.dependentList.count() > 0) {
    dependentList.dependentList.forEach(dependent => {
      if (dependent.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `Please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
        });
      }
    });
  }

  if (findHeightWeightAnswer(questions)) {
    if (questions.height?.feet === '' || questions.height?.feet === undefined) {
      messages.push({
        type: 'warning',
        value: 'Missing the applicant height (feet).',
      });
    }
    if (questions.weight === '' || questions.weight === undefined) {
      messages.push({
        type: 'warning',
        value: 'Missing the applicant weight (lbs).',
      });
    }

    if (dependentList.dependentList.count() > 0) {
      dependentList.dependentList.forEach(dependent => {
        if (dependent.relationship.value.toUpperCase() === 'SPOUSE') {
          if (questions.spouseHeight?.feet === '' || questions.spouseHeight?.feet === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Spouse height (feet).',
            });
          }

          if (questions.spouseWeight === '' || questions.spouseWeight === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Spouse weight (lbs).',
            });
          }
        }
        if (dependent.relationship.value.toUpperCase() === 'CHILD') {
          if (questions.childHeight?.feet === '' || questions.childHeight?.feet === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Child height (feet).',
            });
          }

          if (questions.childWeight === '' || questions.childWeight === undefined) {
            messages.push({
              type: 'warning',
              value: 'Missing the Child weight (lbs).',
            });
          }
        }
      });
    }
  }

  if (
    !isNonEmptyString(criticalIllness.spouseSC) &&
    (criticalIllness.planType.value === 'FAMILY' || criticalIllness.planType.value === 'INDSPS')
  ) {
    messages.push({
      type: 'warning',
      value: 'Missing Critical Illness spouse smoking status',
    });
  }

  if (directBill && (Number(criticalIllness.cwa.value) === 0 || criticalIllness.cwa.value === '')) {
    messages.push({type: 'warning', value: 'Missing Critical Illness CWA with direct billing'});
  }

  if (billingAgent.issueDate === '') {
    if (Number(criticalIllness.cwa.value) === 0 || criticalIllness.cwa.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing issue date with CWA for Critical Illness policy',
      });
    } else {
      messages.push({
        type: 'warning',
        value: 'Missing issue date',
      });
    }
  }
  if (billingAgent.dateSigned === '') {
    messages.push({
      type: 'warning',
      value: 'Missing app signed date.',
    });
  }

  if (criticalIllness.replacement && !isValidPolicyNumberPaperElectronic(criticalIllness.policyNumber)) {
    messages.push({
      type: 'warning',
      value: 'Missing replacement policy number with replacement option selected for CI',
    });
  }

  if (childDependents(dependentList).size === 0 && criticalIllness.planType.value === 'INDCH') {
    messages.push({
      type: 'warning',
      value: 'Single-parent Critical Illness policy selected, but no children entered',
    });
  }

  if (childDependents(dependentList).size === 0 && criticalIllness.planType.value === 'FAMILY') {
    messages.push({
      type: 'warning',
      value: 'Family Critical Illness policy selected, but no children entered',
    });
  }

  if (spouseDependent(dependentList).size === 0 && criticalIllness.planType.value === 'FAMILY') {
    messages.push({
      type: 'warning',
      value: 'Family Critical Illness policy selected, but no spouse entered',
    });
  }

  if (dependentList.dependentList.size > 0 && criticalIllness.planType.value === 'IND') {
    messages.push({type: 'warning', value: 'Individual coverage selected with covered dependents'});
  }

  if (spouseDependent(dependentList).size === 0 && criticalIllness.planType.value === 'INDSPS') {
    messages.push({
      type: 'warning',
      value: 'Emp+spouse coverage selected without covered spouse information',
    });
  }

  if (dependentList.dependentList.size > 9) {
    messages.push({type: 'warning', value: 'Too many dependents for Critical Illness plan'});
  }

  dependentList.dependentList.forEach(depItem => {
    if (depItem !== null) {
      if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
        messages.push({
          type: 'info',
          value: 'Primary Insured and spouse are same sex',
        });
    }
  });
  const depCount = dependentList.dependentList.filter(dep => dep.dob === '').size;

  if (depCount > 0) {
    messages.push({
      type: 'warning',
      value: 'Invalid birthdate entered for dependent',
    });
  }

  if (!isNonEmptyString(billingAgent.controlNumber)) {
    messages.push({
      type: 'warning',
      value: 'Missing control number',
    });
  }

  if (formNumber === '') {
    messages.push({
      type: 'warning',
      value: 'Missing form number',
    });
  }
  if (agents.agentList.size === 0) {
    messages.push({
      type: 'warning',
      value: 'Missing agent information',
    });
  }
  if (!isQuality) {
    if (answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
  }
  if (!billingAgent.ink) {
    messages.push({
      type: 'info',
      value: 'Missing primary insured signature',
    });
  }
  if (applicantEmailAddress?.address === '') {
    messages.push({
      type: 'warning',
      value: "Invalid applicant's Email Address.",
    });
  }
  if (stateSigned === '') {
    messages.push({
      type: 'warning',
      value: 'Missing Situs State',
    });
  }
  if (applicantInfo.sex === '') {
    messages.push({
      type: 'warning',
      value: 'Missing sex for primary insured',
    });
  }
  if (agents.agentList.size > 0) {
    let signed = agents.agentList.filter(item => item.signed);

    if (signed.size === 0) {
      messages.push({
        type: 'warning',
        value: '"Agent Signed" check box was not selected',
      });
    }
  }
  const applicantDateOfHire = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.hireDate,
    _: () => null,
  })(applicantInfo);

  if (!isQuality && applicantDateOfHire === '') {
    messages.push({
      type: 'warning',
      value: "Missing the applicant's date of hire.",
    });
  }

  //Applicant Validation
  if (!isNonEmptyString(applicantInfo.name)) {
    messages.push({
      type: 'warning',
      value: 'Missing name for primary insured',
    });
  }
  if (applicantInfo.socialSecurityNumber.value === '') {
    messages.push({
      type: 'warning',
      value: 'Missing social security number for primary insured.',
    });
  }
  if (applicantInfo.sex === '') {
    messages.push({
      type: 'warning',
      value: 'Missing sex for primary insured',
    });
  }
  const results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  return messages;
};

export const getRequiredRiderValuesGUL = (values: string[]) => {
  let riderValuesGUL: string[] = [];
  if (values.length > 0) {
    values.map((item, idx) => {
      if (item.toLowerCase() === 'acceldbteminalill') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.accelDbTeminalIll];
      }
      if (item.toLowerCase() === 'acceldblongtermcare') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.accelDbLongTermCare];
      }
      if (item.toLowerCase() === 'childrenterm') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.childrenTerm];
      }
      if (item.toLowerCase() === 'accidentaldeathbenefit') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.accidentalDeathBenefit];
      }
      if (item.toLowerCase() === 'extbenefits') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.extBenefits];
      }
      if (item.toLowerCase() === 'guaci50') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.guaci50];
      }
      if (item.toLowerCase() === 'guaci100') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.guaci100];
      }
      if (item.toLowerCase() === 'guaci100') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.guaci100];
      }
      if (item.toLowerCase() === 'levelterminsurance') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.levelTermInsurance];
      }
      if (item.toLowerCase() === 'otherinsuredterm ') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.otherInsuredTerm];
      }
      if (item.toLowerCase() === 'payorwaiver') {
        riderValuesGUL = [...riderValuesGUL, GroupULType.payorWaiver];
      }
    });
  }
  return riderValuesGUL;
};

export const getRiderValuesGLTerm = (values: string[], plan: StringUnion) => {
  let riderValuesGLTerm: string[] = [];
  if (values.length > 0) {
    values.map((item, idx) => {
      if (item.toLowerCase() === 'acceldbteminalill') {
        riderValuesGLTerm = [...riderValuesGLTerm, GroupGLTermType.accelDbTeminalIll];
      }
      if (item.toLowerCase() === 'accidentaldeathbenefit') {
        riderValuesGLTerm = [...riderValuesGLTerm, GroupGLTermType.accidentalDeathBenefit];
      }
      if (item.toLowerCase() === 'childrenterm' && plan?.value === 'GLTERM') {
        riderValuesGLTerm = [...riderValuesGLTerm, GroupGLTermType.childrenGLTerm];
      }
      if (item.toLowerCase() === 'childrenterm' && (plan?.value === 'SGLTERM' || plan?.value === 'GIGLTERM')) {
        riderValuesGLTerm = [...riderValuesGLTerm, GroupGLTermType.childrenNoGLTerm];
      }
      if (item.toLowerCase() === 'futurepurchaseoption') {
        riderValuesGLTerm = [...riderValuesGLTerm, GroupGLTermType.futurePurchaseOption];
      }
      if (item.toLowerCase() === 'payorwaiver') {
        riderValuesGLTerm = [...riderValuesGLTerm, GroupGLTermType.payorWaiver];
      }
    });
  }
  return riderValuesGLTerm;
};

export const getRiderValuesIWL = (values: string[]) => {
  let riderValuesGWL: string[] = [];
  if (values.length > 0) {
    values.map((item, idx) => {
      if (item.toLowerCase() === 'accidentaldeathbenefit') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.accidentalDeathBenefit];
      }
      if (item.toLowerCase() === 'premiumwaiver') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.premiumWaiver];
      }
      if (item.toLowerCase() === 'accidentaldeathbenefitterminalillness') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.accidentalDeathBenefitTerminalIllness];
      }
      if (item.toLowerCase() === 'childrenterm') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.childrenTerm];
      }
      if (item.toLowerCase() === 'levelterm20yr') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.levelTerm20yr];
      }
      if (item.toLowerCase() === 'longtermaccel') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.longTermAccel];
      }
      if (item.toLowerCase() === 'spousetermrider') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.spouseTermRider];
      }
      if (item.toLowerCase() === 'longtermaccelextension') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.longTermCareAccelwithExtension];
      }
      if (item.toLowerCase() === 'longtermaccelrestoration') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.longTermCareAccelwithRestoration];
      }
      if (item.toLowerCase() === 'longtermaccelrestorationextension') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.longTermCareAccelwithRestAndExt];
      }
      if (item.toLowerCase() === 'chronicillnessaccel50') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.chronicIllnessAccel50];
      }
      if (item.toLowerCase() === 'chronicillnessaccel100') {
        riderValuesGWL = [...riderValuesGWL, GroupIWLType.chronicIllnessAccel100];
      }
    });
  }
  return riderValuesGWL;
};

export const validateGrpUL = (
  groupUL: GroupUL,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  applicantInfo: ApplicantDemographics,
  agents: AgentList,
  questions: UWQuestions,
  planCodes: PlanCodes,
  caseDetails: CaseDetails,
  owner: OwnerInfo,
  isQuality: boolean
) => {
  const messages: messageType[] = [];
  let isAccidentDeathBenefit = false;
  const answerCount = questions.answers.filter(answer => answer !== '').size;
  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    NewYork: NYApp => NYApp.signatureState.value,
    Group: GrpApp => GrpApp.state.value,
  })(billingAgent);

  const applicantDateOfHire = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.hireDate,
    _: () => null,
  })(applicantInfo);

  const applicantDateOfBirth = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.dateOfBirth,
    _: () => null,
  })(applicantInfo);

  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: GrpApp => (GrpApp.formNumber.key === 'UserAssignedFormNumber' ? GrpApp.formNumber.value : ''),
  })(billingAgent);

  const applicantType = matchApplicantDemographicsByType({
    Ordinary: () => '',
    Group: groupApp => groupApp.type,
    NewYork: () => '',
  })(applicantInfo);

  const applicantAge = applicantInfo.dateOfBirth !== '' ? DateUtility.getAgeFromDob(applicantInfo.dateOfBirth) : 0;

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(groupUL.plan)) {
      messages.push({
        type: 'error',
        value: 'Missing Universal Life Plan Code',
      });
    }

    let {billingCode} = caseDetails as ValidGroupCaseDetails;

    if (billingAgent.billingMode.value === '' && billingCode?.value === '') {
      messages.push({
        type: 'error',
        value: 'Missing billing mode',
      });
    }
    if (!isNonEmptyString(applicantInfo.state)) {
      messages.push({
        type: 'warning',
        value: 'Missing state of residence for primary insured',
      });
    }

    if (!isNonEmptyString(groupUL.faceAmt)) {
      messages.push({
        type: 'warning',
        value: 'Missing universal life face amount',
      });
    }
    if (!isNonEmptyString(groupUL.premium)) {
      messages.push({
        type: 'warning',
        value: 'Missing universal life premium',
      });
    }

    if (!isQuality && answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
    // applicant validation
    if (!isNonEmptyString(applicantInfo.name)) {
      messages.push({
        type: 'warning',
        value: 'Missing name for primary insured',
      });
    }
    if (applicantInfo.socialSecurityNumber.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing social security number for primary insured.',
      });
    }
    if (applicantInfo.sex === '') {
      messages.push({
        type: 'warning',
        value: 'Missing sex for primary insured',
      });
    }
    if (!isNonEmptyString(applicantInfo.address)) {
      messages.push({
        type: 'warning',
        value: 'Missing address information',
      });
    }
    if (!isNonEmptyString(billingAgent.achAccountNumber) && billingAgent.pac === true) {
      messages.push({
        type: 'error',
        value: 'Missing ACH Account number',
      });
    }
  
    if (!isNonEmptyString(billingAgent.achTransitNumber) && billingAgent.pac === true) {
      messages.push({
        type: 'error',
        value: 'Missing ACH Transit number',
      });
    }
    if (!isNonEmptyString(applicantInfo.city)) {
      messages.push({
        type: 'warning',
        value: 'Missing city for primary insured',
      });
    }
    if (stateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing Situs State',
      });
    }
    if (groupUL.replacement) {
      if (groupUL.commission.value === '') {
        messages.push({
          type: 'error',
          value: 'Missing renewal commission value of radio button',
        });
      }
    }

    if (!isQuality) {
      if (applicantDateOfHire === '') {
        messages.push({
          type: 'warning',
          value: 'Missing hiredate for primary insured',
        });
      }
    }

    if (applicantDateOfBirth === '') {
      messages.push({
        type: 'warning',
        value: 'Missing birthdate for primary insured',
      });
    }

    const resultsDOB = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
    if (resultsDOB != null) messages.push({type: 'warning', value: resultsDOB});

    const resultsStreetAddress = validatePrimaryAddress(applicantInfo.address);
    if (resultsStreetAddress != null) messages.push({type: 'warning', value: resultsStreetAddress});

    const resultsZip = validatePrimaryZip(applicantInfo.zip.value);
    if (resultsZip != null) messages.push({type: 'warning', value: resultsZip});

    // owner validation
    if (owner.socialSecurityNumber.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner SSN',
      });
    }
    if (owner.address.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner street address',
      });
    }
    if (owner.city.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner city',
      });
    }
    if (owner.state.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner state',
      });
    }
    if (owner.zip.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner zip code',
      });
    }
    const resultsOwnerSigned = validateAppSignature(owner);
    if (resultsOwnerSigned != null) messages.push({type: 'info', value: resultsOwnerSigned});

    const resultsAgent = validateAgents(agents);
    if (resultsAgent != null) messages.push({type: 'warning', value: resultsAgent});

    const resultsAgentSign = validateAgentSignature(agents);
    if (resultsAgentSign != null) messages.push({type: 'info', value: resultsAgentSign});

    if (billingAgent.issueDate === '') {
      if (Number(groupUL.cwa.value) === 0 || groupUL.cwa.value === '') {
        messages.push({
          type: 'warning',
          value: 'Missing issue date with CWA for universal life policy',
        });
      } else {
        messages.push({
          type: 'warning',
          value: 'Missing issue date',
        });
      }
    }
    if (billingAgent.dateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing app signed date.',
      });
    }
    if (!isNonEmptyString(billingAgent.controlNumber)) {
      messages.push({
        type: 'warning',
        value: 'Missing control number',
      });
    }
    if (!isNonEmptyString(billingAgent.payor)) {
      messages.push({
        type: 'warning',
        value: 'Missing Payors Name',
      });
    }
    if (!billingAgent.ink) {
      messages.push({
        type: 'info',
        value: 'Missing primary insured signature',
      });
    }
    if (formNumber === '') {
      messages.push({
        type: 'warning',
        value: 'Missing form number',
      });
    }

    const replacementPolicyNumber = matchULByType({
      Ordinary: () => '',
      Group: groupApp => (groupApp.policyNumber.key === 'ValidPolicyNumber' ? groupApp.policyNumber.value.value : ''),
      NewYork: () => '',
    })(groupUL);

    if (groupUL?.adb) {
      isAccidentDeathBenefit = true;
    }
    if (isAccidentDeathBenefit && (groupUL.faceAmt.value === '' || Number(groupUL.faceAmt.value) === 0)) {
      messages.push({type: 'warning', value: 'Missing  Accidental death benefit volume'});
    }

    if (groupUL.adb) {
      if (applicantType === 'Child') {
        messages.push({
          type: 'warning',
          value: 'ADB rider is not available on child policies.',
        });
      } else {
        if (applicantAge < 18) {
          messages.push({
            type: 'warning',
            value: 'ADB supplemental benefit requires the age of the primary insured to be 18 or older',
          });
        }
      }
    }
    if (groupUL.waiver) {
      if (applicantAge < 18) {
        messages.push({
          type: 'warning',
          value: 'PW Supplemental Benefit requires the age of the Primary Insured to be 18 or older',
        });
      }
    }
    if (isNonEmptyString(groupUL.priInsured)) {
      const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
      if (questionList !== '') {
        const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'TOBACCOUSE');
        const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
        const insuredSCAnswer = groupUL.priInsured.value === 'Smoker';
        if (tobaccoAnswer !== insuredSCAnswer) {
          messages.push({
            type: 'warning',
            value: 'Primary Insured Smoker selection and Tobacco Usage question must have the same response ',
          });
        }
      }
    }

    if (isNonEmptyString(groupUL.spouseInsured)) {
      const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
      if (questionList !== '') {
        const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'SPOUSETOBACCOUSE');
        const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
        const spouseSCAnswer = groupUL.spouseInsured.value === 'Smoker';
        if (tobaccoAnswer !== spouseSCAnswer) {
          messages.push({
            type: 'warning',
            value: 'Secondary Insured Smoker selection and Tobacco Usage question must have the same response ',
          });
        }
      }
    }

    groupUL.riderInformation.codes.forEach(riderCode => {
      if (riderCode?.toUpperCase() === 'GUCTR' && childDependents(dependentList).size === 0) {
        messages.push({
          type: 'warning',
          value: 'Missing child information for Universal Life Child Rider',
        });
      }
      if (riderCode?.toUpperCase() === 'GUOIR' && spouseDependent(dependentList).size === 0) {
        messages.push({
          type: 'warning',
          value: 'Missing Spouse Information for Universal Life Spouse Rider',
        });
      }
    });

    let gultcRider = groupUL.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GULTC');
    let gulTextRider = groupUL.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GULTCEXT+GULTC');
    if (gultcRider && gulTextRider) {
      if (applicantInfo.state.value === 'TX') {
        messages.push({
          type: 'warning',
          value: 'Extension of Benefits (GULTCEXT+GULTC) cannot be selected with Long Term Care (GULTC) for TX state ',
        });
      }
    }
    dependentList.dependentList.forEach(depItem => {
      if (depItem !== null) {
        if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
          messages.push({
            type: 'info',
            value: 'Primary Insured and spouse are same sex',
          });
      }
    });

    if (dependentList.dependentList.count() > 0) {
      dependentList.dependentList.forEach(dependent => {
        if (dependent.socialSecurityNumber.value === '') {
          messages.push({
            type: 'warning',
            value: `Please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
          });
        }
      });
    }

    if (groupUL.plan.value === 'GUL23' || groupUL.plan.value === 'SUL23' || groupUL.plan.value === 'GIUL23') {
      let packageItem = isLoaded(groupUL.packageList!)
        ? groupUL.packageList!.Data.find(
            (packageItem: object) => packageItem['packageName'] === groupUL.packageName!.value
          )
        : null;
      let requiredRiderValues: string[] = [];
      let notOfferedRiderValues: string[] = [];
      if (packageItem !== null || packageItem !== undefined) {
        for (let key in packageItem) {
          let value = packageItem[key];
          if (value === '0' && key !== 'accidentalDeathBenefit' && key !== 'payorWaiver' && key !== 'guaci100') {
            requiredRiderValues.push(key);
          }
          if (value === '2') {
            notOfferedRiderValues.push(key);
          }
        }
      }

      let translatedRequiredRiderValues = getRequiredRiderValuesGUL(requiredRiderValues);
      let translatedNotOfferedRiderValues = getRequiredRiderValuesGUL(notOfferedRiderValues);

      let count = compareSelectedandKeyerRiders(translatedRequiredRiderValues, groupUL.riderInformation.codes);

      if (count !== translatedRequiredRiderValues.length) {
        messages.push({
          type: 'warning',
          value: 'All required riders do not match Keyer Sheet configuration.',
        });
      }
      const notOfferedRider = groupUL.riderInformation.codes.some(rider =>
        translatedNotOfferedRiderValues.some(item => item === rider)
      );
      if (notOfferedRider) {
        messages.push({
          type: 'warning',
          value: 'Riders selection does not match Keyer Sheet configuration.',
        });
      }
    }
  }
  return messages;
};

export const validateGrpGLTerm = (
  glTerm: GroupGLTERM,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  applicantInfo: ApplicantDemographics,
  agents: AgentList,
  questions: UWQuestions,
  planCodes: PlanCodes,
  caseDetails: CaseDetails,
  owner: OwnerInfo,
  isQuality: boolean
) => {
  const messages: messageType[] = [];
  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    NewYork: NYApp => NYApp.signatureState.value,
    Group: GrpApp => GrpApp.state.value,
  })(billingAgent);

  const applicantDateOfHire = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.hireDate,
    _: () => null,
  })(applicantInfo);

  const applicantDateOfBirth = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.dateOfBirth,
    _: () => null,
  })(applicantInfo);
  const applicantType = matchApplicantDemographicsByType({
    Ordinary: () => '',
    Group: groupApp => groupApp.type,
    NewYork: () => '',
  })(applicantInfo);
  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: GrpApp => GrpApp.email,
    _: () => null,
  })(applicantInfo);
  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: GrpApp => (GrpApp.formNumber.key === 'UserAssignedFormNumber' ? GrpApp.formNumber.value : ''),
  })(billingAgent);

  const applicantAge = applicantInfo.dateOfBirth !== '' ? DateUtility.getAgeFromDob(applicantInfo.dateOfBirth) : 0;
  const answerCount = questions.answers.filter(answer => answer !== '').size;
  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(glTerm.plan)) {
      messages.push({
        type: 'error',
        value: 'Missing GL term Plan Code',
      });
    }

    let {billingCode} = caseDetails as ValidGroupCaseDetails;

    if (billingAgent.billingMode.value === '' && billingCode?.value === '') {
      messages.push({
        type: 'error',
        value: 'Missing Billing Mode',
      });
    }
    if (!isNonEmptyString(applicantInfo.state)) {
      messages.push({
        type: 'warning',
        value: 'Missing state of residence for primary insured',
      });
    }

    if (!isNonEmptyString(glTerm.faceAmt)) {
      messages.push({
        type: 'warning',
        value: 'Missing GLTERM face amount',
      });
    }
    if (!isNonEmptyString(glTerm.premium)) {
      messages.push({
        type: 'warning',
        value: 'Missing GLTERM premium',
      });
    }

    if (isNonEmptyString(glTerm.priInsured)) {
      const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
      if (questionList !== '') {
        const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'TOBACCOUSE');
        const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
        const insuredSCAnswer = glTerm.priInsured.value === 'Smoker';
        if (tobaccoAnswer !== insuredSCAnswer) {
          messages.push({
            type: 'warning',
            value: 'Primary Insured Smoker selection and Tobacco Usage question must have the same response ',
          });
        }
      }
    }

    if (isNonEmptyString(glTerm.spouseInsured)) {
      const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
      if (questionList !== '') {
        const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'SPOUSETOBACCOUSE');
        const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
        const spouseSCAnswer = glTerm.spouseInsured.value === 'Smoker';
        if (tobaccoAnswer !== spouseSCAnswer) {
          messages.push({
            type: 'warning',
            value: 'Secondary Insured Smoker selection and Tobacco Usage question must have the same response ',
          });
        }
      }
    }

    if (!isQuality && answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
    // applicant validation
    if (!isNonEmptyString(applicantInfo.name)) {
      messages.push({
        type: 'warning',
        value: 'Missing name for primary insured',
      });
    }
    if (applicantEmailAddress?.address === '') {
      messages.push({
        type: 'warning',
        value: "Missing applicant's Email Address.",
      });
    }
    if (applicantInfo.socialSecurityNumber.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing social security number for primary insured.',
      });
    }
    if (applicantInfo.sex === '') {
      messages.push({
        type: 'warning',
        value: 'Missing sex for primary insured',
      });
    }
    if (!isNonEmptyString(applicantInfo.address)) {
      messages.push({
        type: 'warning',
        value: 'Missing address information',
      });
    }
    if (!isNonEmptyString(billingAgent.achAccountNumber) && billingAgent.pac === true) {
      messages.push({
        type: 'error',
        value: 'Missing ACH Account number',
      });
    }
  
    if (!isNonEmptyString(billingAgent.achTransitNumber) && billingAgent.pac === true) {
      messages.push({
        type: 'error',
        value: 'Missing ACH Transit number',
      });
    }
    if (!isNonEmptyString(applicantInfo.city)) {
      messages.push({
        type: 'warning',
        value: 'Missing city for primary insured',
      });
    }

    if (stateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing Situs State',
      });
    }

    if (glTerm.replacement) {
      if (glTerm.commission.value === '') {
        messages.push({
          type: 'error',
          value: 'Missing renewal commission value of radio button',
        });
      }
    }

    if (!isQuality) {
      if (applicantDateOfHire === '') {
        messages.push({
          type: 'warning',
          value: 'Missing hiredate for primary insured',
        });
      }
    }

    if (applicantDateOfBirth === '') {
      messages.push({
        type: 'warning',
        value: 'Missing birthdate for primary insured',
      });
    }

    // owner validation
    if (owner.socialSecurityNumber.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner SSN',
      });
    }
    if (owner.address.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner street address',
      });
    }
    if (owner.city.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner city',
      });
    }
    if (owner.state.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner state',
      });
    }
    if (owner.zip.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner zip code',
      });
    }
    const resultsOwnerSigned = validateAppSignature(owner);
    if (resultsOwnerSigned != null) messages.push({type: 'info', value: resultsOwnerSigned});

    let isAccidentDeathBenefit = false;
    if (glTerm?.adb) {
      isAccidentDeathBenefit = true;
    }
    if (isAccidentDeathBenefit && (glTerm.faceAmt.value === '' || Number(glTerm.faceAmt.value) === 0)) {
      messages.push({type: 'warning', value: 'Missing GLTERM Accidental death benefit volume'});
    }

    if (glTerm.adb) {
      if (applicantAge < 18) {
        messages.push({
          type: 'warning',
          value: 'ADB supplemental benefit requires the age of the primary insured to be 18 or older',
        });
        if (glTerm.priInsured.value === 'Smoker') {
          messages.push({
            type: 'warning',
            value: 'Smoker status requires the age of the primary insured to be 18 or older.',
          });
        }
      }
    }
    if (glTerm.premiumWaiver) {
      if (applicantAge < 18) {
        messages.push({
          type: 'warning',
          value: 'PW Supplemental Benefit requires the age of the Primary Insured to be 18 or older',
        });
      }
    }

    if (billingAgent.issueDate === '') {
      if (Number(glTerm.cwa.value) !== 0 || glTerm.cwa.value !== '') {
        messages.push({
          type: 'warning',
          value: 'Missing Issue Date with CWA for GLTERM Policy',
        });
      }
    }

    if (billingAgent.issueDate === '') {
      messages.push({
        type: 'warning',
        value: 'Missing issue date',
      });
    }
    if (billingAgent.dateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing app signed date.',
      });
    }
    const resultsAgent = validateAgents(agents);
    if (resultsAgent != null) messages.push({type: 'warning', value: resultsAgent});

    const resultsAgentSign = validateAgentSignature(agents);
    if (resultsAgentSign != null) messages.push({type: 'info', value: resultsAgentSign});

    if (!isNonEmptyString(billingAgent.controlNumber)) {
      messages.push({
        type: 'warning',
        value: 'Missing control number',
      });
    }
    if (!isNonEmptyString(billingAgent.payor)) {
      messages.push({
        type: 'warning',
        value: 'Missing Payors Name',
      });
    }
    if (!billingAgent.ink) {
      messages.push({
        type: 'info',
        value: 'Missing primary insured signature',
      });
    }
    if (formNumber === '') {
      messages.push({
        type: 'warning',
        value: 'Missing form number',
      });
    }

    glTerm.riderInformation.codes.forEach(riderCode => {
      if (
        (riderCode?.toUpperCase() === 'CTPCTR' || riderCode?.toUpperCase() === 'CTPCTZ') &&
        childDependents(dependentList).size === 0
      ) {
        messages.push({
          type: 'warning',
          value: 'Missing Child Information for GLTERM Child Rider',
        });
      }
    });

    dependentList.dependentList.forEach(depItem => {
      if (depItem !== null) {
        if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
          messages.push({
            type: 'info',
            value: 'Primary Insured and spouse are same sex',
          });
      }
    });

    if (dependentList.dependentList.count() > 0) {
      dependentList.dependentList.forEach(dependent => {
        if (dependent.socialSecurityNumber.value === '') {
          messages.push({
            type: 'warning',
            value: `Please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
          });
        }
      });
    }

    let packageItem = isLoaded(glTerm.packageList!) ? glTerm.packageList!.Data[0] : null;
    let requiredRiderValues: string[] = [];
    let notOfferedRiderValues: string[] = [];
    if (packageItem !== null || packageItem !== undefined) {
      for (let key in packageItem) {
        let value = packageItem[key];
        if (value === '0' && key !== 'accidentalDeathBenefit' && key !== 'payorWaiver') {
          requiredRiderValues.push(key);
        }
        if (value === '2') {
          notOfferedRiderValues.push(key);
        }
      }
    }

    let translatedRequiredRiderValues = getRiderValuesGLTerm(requiredRiderValues, glTerm.plan);
    let translatedNotOfferedRiderValues = getRiderValuesGLTerm(notOfferedRiderValues, glTerm.plan);

    let count = compareSelectedandKeyerRiders(translatedRequiredRiderValues, glTerm.riderInformation.codes);
    if (count !== translatedRequiredRiderValues.length) {
      messages.push({
        type: 'warning',
        value: 'All required riders do not match Keyer Sheet configuration.',
      });
    }

    const notOfferedRider = glTerm.riderInformation.codes.some(rider =>
      translatedNotOfferedRiderValues.some(item => item === rider)
    );

    if (notOfferedRider) {
      messages.push({
        type: 'warning',
        value: 'Riders selection does not match Keyer Sheet configuration.',
      });
    }
    return messages;
  }
  return messages;
};

export const ValidateGroupIWL = (
  gwl: GroupIWL,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  applicantInfo: ApplicantDemographics,
  agents: AgentList,
  questions: UWQuestions,
  planCodes: PlanCodes,
  caseDetails: CaseDetails,
  owner: OwnerInfo,
  isQuality: boolean
) => {
  const messages: messageType[] = [];
  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    NewYork: NYApp => NYApp.signatureState.value,
    Group: GrpApp => GrpApp.state.value,
  })(billingAgent);

  const applicantDateOfHire = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.hireDate,
    _: () => null,
  })(applicantInfo);

  const applicantDateOfBirth = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.dateOfBirth,
    _: () => null,
  })(applicantInfo);
  const applicantType = matchApplicantDemographicsByType({
    Ordinary: () => '',
    Group: groupApp => groupApp.type,
    NewYork: () => '',
  })(applicantInfo);
  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: GrpApp => GrpApp.email,
    _: () => null,
  })(applicantInfo);
  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: GrpApp => (GrpApp.formNumber.key === 'UserAssignedFormNumber' ? GrpApp.formNumber.value : ''),
  })(billingAgent);

  const applicantAge = applicantInfo.dateOfBirth !== '' ? DateUtility.getAgeFromDob(applicantInfo.dateOfBirth) : 0;
  const answerCount = questions.answers.filter(answer => answer !== '').size;
  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(gwl.plan)) {
      messages.push({
        type: 'error',
        value: 'Missing GWL Plan Code',
      });
    }

    dependentList.dependentList.forEach(depItem => {
      if (depItem !== null) {
        if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
          messages.push({
            type: 'info',
            value: 'Primary Insured and spouse are same sex',
          });
      }
    });

    let {billingCode} = caseDetails as ValidGroupCaseDetails;

    if (billingAgent.billingMode.value === '' && billingCode?.value === '') {
      messages.push({
        type: 'error',
        value: 'Missing Billing Mode',
      });
    }
    let results = validatePrimaryState(applicantInfo.state);
    if (results != null) messages.push({type: 'warning', value: results});
    if (stateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing Situs State',
      });
    }

    if (gwl.replacement && !isValidPolicyNumberPaperElectronic(gwl.policyNumber)) {
      messages.push({
        type: 'warning',
        value: 'Missing Replacement Policy Number',
      });
    }

    if (!isNonEmptyString(gwl.faceAmt)) {
      messages.push({
        type: 'warning',
        value: 'Missing GWL face amount',
      });
    }
    if (!isNonEmptyString(gwl.premium)) {
      messages.push({
        type: 'warning',
        value: 'Missing GWL premium',
      });
    }

    if (isNonEmptyString(gwl.priInsured)) {
      const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
      if (questionList !== '') {
        const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'TOBACCOUSE');
        const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
        const insuredSCAnswer = gwl.priInsured.value === 'Smoker';
        const insuredNotPresent = gwl.priInsured.value === 'Not Present';

        if (!insuredNotPresent) {
          if (tobaccoAnswer !== insuredSCAnswer) {
            messages.push({
              type: 'warning',
              value: 'Primary Insured Smoker selection and Tobacco Usage question must have the same response ',
            });
          }
        }
      }
    }

    if (isNonEmptyString(gwl.spouseInsured)) {
      const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
      if (questionList !== '') {
        const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'SPOUSETOBACCOUSE');
        const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
        const spouseSCAnswer = gwl.spouseInsured.value === 'Smoker';
        const spouseNotPresent = gwl.spouseInsured.value === 'Not Present';
        if (!spouseNotPresent) {
          if (tobaccoAnswer !== spouseSCAnswer) {
            messages.push({
              type: 'warning',
              value: 'Secondary Insured Smoker selection and Tobacco Usage question must have the same response ',
            });
          }
        }
      }
    }

    if (!isQuality && answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
    // applicant validation
    if (!isNonEmptyString(applicantInfo.name)) {
      messages.push({
        type: 'warning',
        value: 'Missing name for primary insured',
      });
    }
    if (applicantEmailAddress?.address === '') {
      messages.push({
        type: 'warning',
        value: "Missing applicant's Email Address.",
      });
    }
    if (applicantInfo.socialSecurityNumber.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing social security number for primary insured.',
      });
    }
    if (applicantInfo.sex === '') {
      messages.push({
        type: 'warning',
        value: 'Missing sex for primary insured',
      });
    }
    if (!isNonEmptyString(applicantInfo.address)) {
      messages.push({
        type: 'warning',
        value: 'Missing address information',
      });
    }
    if (!isNonEmptyString(billingAgent.achAccountNumber) && billingAgent.pac === true) {
      messages.push({
        type: 'error',
        value: 'Missing ACH Account number',
      });
    }
  
    if (!isNonEmptyString(billingAgent.achTransitNumber) && billingAgent.pac === true) {
      messages.push({
        type: 'error',
        value: 'Missing ACH Transit number',
      });
    }
    if (!isNonEmptyString(applicantInfo.city)) {
      messages.push({
        type: 'warning',
        value: 'Missing city for primary insured',
      });
    }

    if (!isQuality) {
      if (applicantDateOfHire === '') {
        messages.push({
          type: 'warning',
          value: 'Missing hiredate for primary insured',
        });
      }
    }

    if (applicantDateOfBirth === '') {
      messages.push({
        type: 'warning',
        value: 'Missing birthdate for primary insured',
      });
    }

    // owner validation
    if (owner.socialSecurityNumber.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner SSN',
      });
    }
    if (owner.address.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner street address',
      });
    }
    if (owner.city.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner city',
      });
    }
    if (owner.state.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner state',
      });
    }
    if (owner.zip.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing owner zip code',
      });
    }
    const resultsOwnerSigned = validateAppSignature(owner);
    if (resultsOwnerSigned != null) messages.push({type: 'info', value: resultsOwnerSigned});

    let isAccidentDeathBenefit = false;
    if (gwl?.accidentalDeathBenefitTerminalIllness) {
      isAccidentDeathBenefit = true;
    }
    if (isAccidentDeathBenefit && (gwl.faceAmt.value === '' || Number(gwl.faceAmt.value) === 0)) {
      messages.push({type: 'warning', value: 'Missing GWL Accidental death benefit volume'});
    }

    if (gwl.accidentalDeathBenefitTerminalIllness) {
      if (applicantAge < 18) {
        messages.push({
          type: 'warning',
          value: 'ADB supplemental benefit requires the age of the primary insured to be 18 or older',
        });
        if (gwl.priInsured.value === 'Smoker') {
          messages.push({
            type: 'warning',
            value: 'Smoker status requires the age of the primary insured to be 18 or older.',
          });
        }
      }
    }
    if (gwl.premiumWaiver) {
      if (applicantAge < 18) {
        messages.push({
          type: 'warning',
          value: 'PW Supplemental Benefit requires the age of the Primary Insured to be 18 or older',
        });
      }
    }

    if (billingAgent.issueDate === '') {
      if (Number(gwl.cwa.value) !== 0 || gwl.cwa.value !== '') {
        messages.push({
          type: 'warning',
          value: 'Missing Issue Date with CWA for GWL Policy',
        });
      }
    }

    if (billingAgent.issueDate === '') {
      messages.push({
        type: 'warning',
        value: 'Missing issue date',
      });
    }
    if (billingAgent.dateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing app signed date.',
      });
    }
    const resultsAgent = validateAgents(agents);
    if (resultsAgent != null) messages.push({type: 'warning', value: resultsAgent});

    const resultsAgentSign = validateAgentSignature(agents);
    if (resultsAgentSign != null) messages.push({type: 'info', value: resultsAgentSign});

    if (!isNonEmptyString(billingAgent.controlNumber)) {
      messages.push({
        type: 'warning',
        value: 'Missing control number',
      });
    }
    if (!isNonEmptyString(billingAgent.payor)) {
      messages.push({
        type: 'warning',
        value: 'Missing Payors Name',
      });
    }
    if (!billingAgent.ink) {
      messages.push({
        type: 'info',
        value: 'Missing primary insured signature',
      });
    }
    if (formNumber === '') {
      messages.push({
        type: 'warning',
        value: 'Missing form number',
      });
    }

    gwl.riderInformation.codes.forEach(riderCode => {
      if (riderCode?.toUpperCase() === 'GWCCT' && childDependents(dependentList).size === 0) {
        messages.push({
          type: 'warning',
          value: 'Missing Child Information for GWL Child Rider',
        });
      }
      if (riderCode?.toUpperCase() === 'GWCST' && spouseDependent(dependentList).size === 0) {
        messages.push({
          type: 'warning',
          value: 'Missing Spouse Information for GWL Spouse Rider',
        });
      }
    });

    if (dependentList.dependentList.count() > 0) {
      dependentList.dependentList.forEach(dependent => {
        if (dependent.socialSecurityNumber.value === '') {
          messages.push({
            type: 'warning',
            value: `Please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
          });
        }
      });
    }
    let gwlLtcaRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCLTC');
    let gwlCiaRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCCI');
    let gwlLCiarRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCLTCR');
    let gwlLCiaeRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCLTCE');
    let gwlCciRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCCI');
    let gwlAdbRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCADB');
    let gwlCtiRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCTI');
    let gwlLCiareRider = gwl.riderInformation.codes.some(riderCode => riderCode?.toUpperCase() === 'GWCLTCRE');

    if (gwlLtcaRider && (gwlCiaRider || gwlLCiarRider || gwlLCiaeRider || gwlLCiareRider)) {
      messages.push({
        type: 'warning',
        value: 'GWCLTC cannot be selected along with GWCCI or GWCLTCR or GWCLTCE or GWCLTCRE',
      });
    }

    if (gwlLCiaeRider && (gwlLtcaRider || gwlLCiarRider || gwlCiaRider || gwlLCiareRider)) {
      messages.push({
        type: 'warning',
        value: 'GWCLTCE cannot be selected along with GWCCI or GWCLTC or GWCLTCR or GWCLTCRE',
      });
    }

    if (gwlLCiarRider && (gwlLtcaRider || gwlCiaRider || gwlLCiaeRider || gwlLCiareRider)) {
      messages.push({
        type: 'warning',
        value: 'GWCLTCR cannot be selected along with GWCCI or GWCLTC or GWCLTCE or GWCLTCRE',
      });
    }

    if (gwlCciRider && (gwlLCiaeRider || gwlLtcaRider || gwlLCiarRider || gwlLCiareRider)) {
      messages.push({
        type: 'warning',
        value: 'GWCCI cannot be selected along with GWCLTCRE or GWCLTC or GWCLTCE or GWCLTCR',
      });
    }

    if (gwlLCiareRider && (gwlLtcaRider || gwlLCiaeRider || gwlLCiarRider || gwlCciRider)) {
      messages.push({
        type: 'warning',
        value: 'GWCLTCRE cannot be selected along with GWCCI or GWCLTC or GWCLTCE or GWCLTCR',
      });
    }

    if (
      applicantInfo.state.value === 'AL' ||
      applicantInfo.state.value === 'AR' ||
      applicantInfo.state.value === 'KY' ||
      applicantInfo.state.value === 'MS' ||
      applicantInfo.state.value === 'OK'
    ) {
      if ((gwlCtiRider && !gwlAdbRider) || (gwlAdbRider && !gwlCtiRider)) {
        messages.push({
          type: 'warning',
          value: `GWCADB to be sold with GWCTI in State ${applicantInfo.state.value}`,
        });
      }
    }
    let requiredRiderValues: string[] = [];
    let notOfferedRiderValues: string[] = [];
    let packageItem = isLoaded(gwl.packageList!) ? gwl.packageList!.Data[0] : null;
    if (packageItem !== null || packageItem !== undefined) {
      for (let key in packageItem) {
        let value = packageItem[key];

        if (value === '0' && key !== 'accidentalDeathBenefitTerminalIllness' && key !== 'premiumWaiver') {
          requiredRiderValues.push(key);
        }
        if (value === '2') {
          notOfferedRiderValues.push(key);
        }
      }
    }

    let translatedRequiredRiderValues = getRiderValuesIWL(requiredRiderValues);
    let translatedNotOfferedRiderValues = getRiderValuesIWL(notOfferedRiderValues);

    let count = compareSelectedandKeyerRiders(translatedRequiredRiderValues, gwl.riderInformation.codes);
    if (count !== translatedRequiredRiderValues.length) {
      messages.push({
        type: 'warning',
        value: 'All required riders do not match Keyer Sheet configuration.',
      });
    }

    const notOfferedRider = gwl.riderInformation.codes.some(rider =>
      translatedNotOfferedRiderValues.some(item => item === rider)
    );

    if (notOfferedRider) {
      messages.push({
        type: 'warning',
        value: 'Riders selection does not match Keyer Sheet configuration.',
      });
    }
    return messages;
  }
  return messages;
};

export const validateGrpShop = (
  shop: GroupShop,
  dependents: DependentLists,
  billingAgent: BillingAgent,
  isQuality: boolean,
  premium: Premium,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  questions: UWQuestions,
  owner: OwnerInfo,
  planCodes: PlanCodes
) => {
  const messages: messageType[] = [];
  let {billingCode, situsState} = caseDetails as ValidGroupCaseDetails;

  const stateSigned = getStateSigned(billingAgent);
  const answerCount = questions.answers.filter(answer => answer !== '').size;

  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: GrpApp => GrpApp.email,
    _: () => null,
  })(applicantInfo);

  const applicantDateOfHire = matchApplicantDemographicsByType({
    Group: GroupApp => GroupApp.hireDate,
    _: () => null,
  })(applicantInfo);

  const riderCodesGroup = matchShopByType({
    Ordinary: ordinaryApp => null,

    Group: groupApp => groupApp.riderInformation.codes,
    NewYork: newYorkApp => null,
  })(shop);

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(shop.planType)) {
      messages.push({type: 'error', value: 'Missing SHOP plan type'});
    }

    if (!isNonEmptyString(shop.packageOption)) {
      messages.push({
        type: 'error',
        value: 'Missing Shop Package',
      });
    }
    if (stateSigned === '') {
      messages.push({
        type: 'warning',
        value: 'Missing Situs State',
      });
    }
  }

  if (
    ((!isNonEmptyString(shop.premium) || isNaN(Number(shop.premium.value))) && !isQuality) ||
    (isQuality && premium.key === 'InvalidPremium')
  ) {
    messages.push({type: 'warning', value: 'Missing SHOP premium'});
  }

  if (Number(shop.cwa.value) > 0 && billingAgent.issueDate === '') {
    messages.push({type: 'warning', value: 'Missing issue date with CWA for SHOP policy'});
  }

  if (shop.replacement && !isValidPolicyNumberPaperElectronic(shop.policyNumber)) {
    messages.push({
      type: 'warning',
      value: 'Missing Replacement Policy Number',
    });
  }
  if (billingAgent.issueDate === '') {
    messages.push({
      type: 'warning',
      value: 'Missing issue date',
    });
  }

  if (!isNonEmptyString(billingAgent.controlNumber)) {
    messages.push({
      type: 'warning',
      value: 'Missing control number',
    });
  }

  if (!isQuality) {
    let results = groupDependentProductIdentifier(caseDetails, shop, dependents, isQuality);
    if (results != null) messages.push({type: 'error', value: results});

    if (answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
  }

  const depCount = dependents.dependentList.filter(dep => dep.dob === '').size;

  if (depCount > 0) {
    messages.push({
      type: 'warning',
      value: 'Missing birthdate entered for dependent',
    });
  }

  if (applicantEmailAddress?.address === '') {
    messages.push({
      type: 'warning',
      value: "Missing applicant's Email Address.",
    });
  }

  if (shop.planType.value === 'FAMILY' && dependents.dependentList.size === 0) {
    messages.push({type: 'warning', value: 'Missing dependents for SHOP family plan'});
  }

  dependents.dependentList.forEach(depItem => {
    if (depItem !== null) {
      if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
        messages.push({
          type: 'info',
          value: 'Primary Insured and spouse are same sex',
        });
    }
  });

  if (dependents.dependentList.count() > 0) {
    dependents.dependentList.forEach(dependent => {
      if (dependent.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `Please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
        });
      }
    });
  }
  if (dependents.dependentList.size > 0 && shop.planType.value === 'IND') {
    messages.push({
      type: 'warning',
      value: 'Individual coverage selected with covered dependents.',
    });
  }

  if (spouseDependent(dependents).size === 0 && shop.planType.value === 'INDSPS') {
    messages.push({
      type: 'warning',
      value: 'Emp+spouse coverage selected without covered spouse information',
    });
  }

  if (childDependents(dependents).size === 0 && shop.planType.value === 'INDCH') {
    messages.push({
      type: 'warning',
      value: 'Emp+child coverage selected without covered dependent information',
    });
  }
  if (shop.planType.value === 'FAMILY' && spouseDependent(dependents).size === 0) {
    messages.push({type: 'warning', value: 'Missing Spouse for Family Shop Plan '});
  }

  if (shop.planType.value === 'INDCH' && childDependents(dependents).size === 0) {
    messages.push({type: 'warning', value: 'Missing children for SHOP indiv. w/child plan'});
  }

  if (shop.planType.value === 'INDSPS' && spouseDependent(dependents).size === 0) {
    messages.push({type: 'warning', value: 'Missing spouse for SHOP indiv. W/Spouse plan'});
  }

  if (dependents.dependentList.size > 9) {
    messages.push({type: 'warning', value: 'Too many dependents for SHOP plan'});
  }

  const isHospRiderPresent = riderCodesGroup!.some(rider => rider === 'HOSP');
  if (shop.plan.value === 'GVSP1') {
    if (isHospRiderPresent === false) {
      messages.push({
        type: 'error',
        value: 'Invalid Units for Hospital Related SHOP Benefit',
      });
    }
  }

  const isSurgeryRiderPresent = riderCodesGroup!.some(rider => rider === 'SURG');
  if (shop.plan.value === 'GVSP1') {
    if (isSurgeryRiderPresent === false) {
      messages.push({
        type: 'error',
        value: 'Invalid Units for Surgery/Inpatient SHOP Benefit',
      });
    }
  }
  const isOutpatientRiderPresent = riderCodesGroup!.some(rider => rider === 'OUTP');
  if (shop.plan.value === 'GVSP1') {
    if (isOutpatientRiderPresent === false) {
      messages.push({
        type: 'error',
        value: 'Invalid Units for Outpatient Related SHOP Benefit',
      });
    }
  }

  const isIcuRiderPresent = riderCodesGroup!.some(rider => rider === 'GVSICU');
  if (shop.plan.value === 'GVSP2') {
    if (isIcuRiderPresent === false) {
      messages.push({
        type: 'error',
        value: 'Invalid Units for ICU Related SHOP Benefit	Missing Units',
      });
    }
  }

  const isHospitalRiderPresent = riderCodesGroup!.some(rider => rider === 'GVSHOSP');
  if (shop.plan.value === 'GVSP2') {
    if (isHospitalRiderPresent === false) {
      messages.push({
        type: 'error',
        value: 'Invalid Units for Hospital Related SHOP 2 Benefit',
      });
    }
  }

  const isInitialRiderPresent = riderCodesGroup!.some(rider => rider === 'GVSP2');
  if (shop.plan.value === 'GVSP2') {
    if (isInitialRiderPresent === false) {
      messages.push({
        type: 'error',
        value: 'Invalid Units for Hospital Confinement Related SHOP 2 Benefit',
      });
    }
  }

  if (!isQuality) {
    if (applicantDateOfHire === '') {
      messages.push({
        type: 'warning',
        value: 'Missing hiredate for primary insured',
      });
    }
  }

  const signatureState = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    Group: () => '',
    NewYork: () => '',
  })(billingAgent);

  //Applicant Validation
  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateBeneficiaryInformation(beneficiary);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  return messages;
};

export const validateGrpDisability = (
  disability: GroupDisability,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  isQuality: boolean,
  premium: Premium,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  questions: UWQuestions,
  owner: OwnerInfo,
  planCodes: PlanCodes
) => {
  const messages: messageType[] = [];

  let {billingCode} = caseDetails as ValidGroupCaseDetails;
  const answerCount = questions.answers.filter(answer => answer !== '').size;

  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    NewYork: NYApp => NYApp.signatureState.value,
    Group: GrpApp => GrpApp.state.value,
  })(billingAgent);

  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: GrpApp => GrpApp.email,
    _: () => null,
  })(applicantInfo);

  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: GrpApp => (GrpApp.formNumber.key === 'UserAssignedFormNumber' ? GrpApp.formNumber.value : ''),
  })(billingAgent);

  if (
    !isNonEmptyString(applicantInfo.address) ||
    !isNonEmptyString(applicantInfo.city) ||
    !isNonEmptyString(applicantInfo.state) ||
    applicantInfo.zip.value === ''
  ) {
    messages.push({
      type: 'error',
      value: 'Missing address information',
    });
  }
  if (!isNonEmptyString(billingAgent.achAccountNumber) && billingAgent.pac === true) {
    messages.push({
      type: 'error',
      value: 'Missing ACH Account number',
    });
  }

  if (!isNonEmptyString(billingAgent.achTransitNumber) && billingAgent.pac === true) {
    messages.push({
      type: 'error',
      value: 'Missing ACH Transit number',
    });
  }
  if (billingAgent.billingMode.value === '' && billingCode?.value === '') {
    messages.push({
      type: 'error',
      value: 'Missing billing mode',
    });
  }

  if (!isQuality) {
    if (answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }
  }

  //Applicant Validation
  if (!isNonEmptyString(applicantInfo.name)) {
    messages.push({
      type: 'warning',
      value: 'Missing name for primary insured',
    });
  }

  if (stateSigned === '') {
    messages.push({
      type: 'warning',
      value: 'Missing Situs State',
    });
  }
  if (!isNonEmptyString(applicantInfo.state)) {
    messages.push({
      type: 'warning',
      value: 'Missing state of residence for primary insured',
    });
  }

  if (applicantInfo.sex === '') {
    messages.push({
      type: 'warning',
      value: 'Missing sex for primary insured',
    });
  }

  if (applicantInfo.socialSecurityNumber.value === '') {
    messages.push({
      type: 'warning',
      value: 'Missing social security number for primary insured',
    });
  }

  if (applicantEmailAddress?.address === '') {
    messages.push({
      type: 'warning',
      value: "Missing applicant's Email Address.",
    });
  }

  if (
    ((!isNonEmptyString(disability.premium) || isNaN(Number(disability.premium.value))) && !isQuality) ||
    (isQuality && premium.key === 'InvalidPremium')
  ) {
    messages.push({type: 'warning', value: 'Missing Disability premium'});
  }

  if (billingAgent.issueDate === '') {
    if (Number(disability.cwa.value) === 0 || disability.cwa.value === '') {
      messages.push({
        type: 'warning',
        value: 'Missing issue date with CWA for Disability policy',
      });
    } else {
      messages.push({
        type: 'warning',
        value: 'Missing issue date',
      });
    }
  }

  if (!isNonEmptyString(billingAgent.controlNumber)) {
    messages.push({
      type: 'warning',
      value: 'Missing control number',
    });
  }

  if (formNumber === '') {
    messages.push({
      type: 'warning',
      value: 'Missing form number',
    });
  }

  const depCount = dependentList.dependentList.filter(dep => dep.dob === '').size;

  if (depCount > 0) {
    messages.push({
      type: 'warning',
      value: 'Invalid birthdate entered for dependent',
    });
  }

  if (dependentList.dependentList.count() > 0) {
    dependentList.dependentList.forEach(dependentList => {
      if (dependentList.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `Please enter an SSN for dependent ${dependentList.firstName.value}, ${dependentList.lastName.value}`,
        });
      }
    });
  }

  if (disability?.gcroprRider == true && disability?.section125 == true) {
    messages.push({
      type: 'warning',
      value: 'GCROPR cannot be selected when GVDIP Section 125 requested.',
    });
  }

  if (disability?.sickness.value === '') {
    messages.push({
      type: 'warning',
      value: 'Missing number of disability sick days.',
    });
  }

  if (disability?.accident.value === '') {
    messages.push({
      type: 'warning',
      value: 'Missing number of disability accident days.',
    });
  }

  if (Number(disability?.monthlyBenefit.value) < 400) {
    messages.push({
      type: 'warning',
      value: 'Monthly Benefit amount must be at least $400.',
    });
  }

  if (disability?.issueType.value == 'SI' && Number(disability?.monthlyBenefit.value) > 6000) {
    messages.push({
      type: 'warning',
      value: 'Monthly Benefit amount exceeds SI limit.',
    });
  }

  if (Number(disability?.monthlyBenefit) / Number(disability?.monthlySalary) > 0.6) {
    messages.push({
      type: 'warning',
      value: 'Monthly Benefit exceeds allowable criteria.',
    });
  }

  const resultsAgent = validateAgents(agents);
  if (resultsAgent != null) messages.push({type: 'warning', value: resultsAgent});

  let results = validatePrimaryInsured(applicantInfo.name);

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  const resultsAgentSign = validateAgentSignature(agents);
  if (resultsAgentSign != null) messages.push({type: 'info', value: resultsAgentSign});

  results = validateSpouseGender(dependentList, applicantInfo);
  if (results != null) messages.push({type: 'info', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateBeneficiaryInformation(beneficiary);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateAppSignature(owner);
    if (results != null) messages.push({type: 'info', value: results});
  }

  if (applicantEmailAddress?.address === '') {
    messages.push({
      type: 'warning',
      value: "Invalid applicant's Email Address.",
    });
  }
};

function compareSelectedandKeyerRiders(translatedReqdValues: string[], riderCodes: List<string>) {
  let equalCount = 0;
  for (let i = 0; i < translatedReqdValues.length; i++) {
    for (let j = 0; j < riderCodes.size; j++) {
      if (translatedReqdValues[i] === riderCodes.toArray()[j]) {
        equalCount++;
      }
    }
  }
  return equalCount;
}
